import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  UsersThree, Umbrella, Bell, CalendarCheck, ArrowSquareOut, Plus, Trash, X, PencilSimple, UserPlus, Warning, CalendarBlank, Gauge, Timer, ClockCountdown, FilePdf,
} from "@phosphor-icons/react";

const LEAVE_LABEL = { odihna: "CO", medical: "Medical", liber: "Zi liberă" };
const LEAVE_TYPES = [["odihna", "Concediu odihnă"], ["medical", "Medical"], ["liber", "Zi liberă"]];
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtShort = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO", { weekday: "short", day: "2-digit", month: "short" }) : "—");
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("ro-RO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function MontajHubPanel() {
  const [projects, setProjects] = useState([]);
  const [montatori, setMontatori] = useState([]);
  const [roster, setRoster] = useState([]);
  const [teams, setTeams] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [running, setRunning] = useState(false);
  const [dash, setDash] = useState(null);
  const [alertHours, setAlertHours] = useState("");
  const [savingAlert, setSavingAlert] = useState(false);
  const [allocProj, setAllocProj] = useState("");
  const [allocTeam, setAllocTeam] = useState("");

  // roster form
  const [fName, setFName] = useState("");
  const [lName, setLName] = useState("");
  const [phone, setPhone] = useState("");
  const [savingR, setSavingR] = useState(false);
  const [leaveFor, setLeaveFor] = useState(null); // roster id with open leave form
  const [lvStart, setLvStart] = useState("");
  const [lvEnd, setLvEnd] = useState("");
  const [lvType, setLvType] = useState("odihna");

  // team form
  const [tName, setTName] = useState("");
  const [tLeader, setTLeader] = useState("");
  const [tNotes, setTNotes] = useState("");
  const [tMembers, setTMembers] = useState([]);
  const [tManual, setTManual] = useState("");
  const [editId, setEditId] = useState(null);
  const [savingT, setSavingT] = useState(false);

  const loadRoster = () => api.get("/montaj/roster").then((r) => setRoster(r.data || [])).catch(() => {});
  const loadTeams = () => api.get("/montaj/teams").then((r) => setTeams(r.data || [])).catch(() => {});
  const loadProjects = () => api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  const loadCalendar = () => api.get("/montaj/calendar?days=14").then((r) => setCalendar(r.data || [])).catch(() => {});
  const loadDash = () => api.get("/montaj/dashboard").then((r) => setDash(r.data || null)).catch(() => {});
  useEffect(() => {
    loadProjects();
    api.get("/montatori").then((r) => setMontatori(r.data || [])).catch(() => {});
    loadRoster(); loadTeams(); loadCalendar(); loadDash();
    api.get("/montaj/alert-config").then((r) => setAlertHours(String(r.data?.window_hours ?? 6))).catch(() => {});
  }, []);

  const saveAlertConfig = async () => {
    const h = parseInt(alertHours, 10);
    if (!h || h < 1) { toast.error("Alege un număr de ore valid"); return; }
    setSavingAlert(true);
    try { await api.put("/montaj/alert-config", { window_hours: h }); toast.success(`Alertă montaj setată la ${h}h înainte`); loadDash(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingAlert(false); }
  };

  const runReminders = async () => {
    setRunning(true);
    try { const { data } = await api.post("/montaj/reminders/run"); toast.success(`Remindere montaj trimise: ${data.reminded}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  // ---- roster ----
  const addRoster = async () => {
    if (!fName.trim() && !lName.trim()) { toast.error("Completează nume și prenume"); return; }
    setSavingR(true);
    try {
      await api.post("/montaj/roster", { first_name: fName, last_name: lName, phone });
      toast.success("Montator adăugat");
      setFName(""); setLName(""); setPhone(""); loadRoster();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingR(false); }
  };
  const delRoster = async (id) => {
    try { await api.delete(`/montaj/roster/${id}`); toast.success("Montator șters"); loadRoster(); loadCalendar(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openLeave = (id) => { setLeaveFor(id); setLvStart(""); setLvEnd(""); setLvType("odihna"); };
  const addLeave = async (id) => {
    if (!lvStart || !lvEnd) { toast.error("Alege perioada de concediu"); return; }
    try {
      await api.post(`/montaj/roster/${id}/leaves`, { start: lvStart, end: lvEnd, type: lvType });
      toast.success("Concediu adăugat"); setLeaveFor(null); loadRoster(); loadCalendar();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delLeave = async (rid, lid) => {
    try { await api.delete(`/montaj/roster/${rid}/leaves/${lid}`); loadRoster(); loadCalendar(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---- teams ----
  const addTeamMember = (id) => {
    const m = roster.find((x) => x.id === id);
    if (!m || tMembers.some((x) => x.roster_id === id)) return;
    setTMembers([...tMembers, { name: m.name, roster_id: m.id }]);
  };
  const addManualMember = () => {
    if (!tManual.trim()) return;
    setTMembers([...tMembers, { name: tManual.trim(), roster_id: null }]);
    setTManual("");
  };
  const removeMember = (i) => setTMembers(tMembers.filter((_, idx) => idx !== i));
  const resetTeam = () => { setTName(""); setTLeader(""); setTNotes(""); setTMembers([]); setEditId(null); };
  const startEditTeam = (t) => {
    setEditId(t.id); setTName(t.name || ""); setTLeader(t.leader || ""); setTNotes(t.notes || "");
    setTMembers((t.members || []).map((m) => ({ name: m.name, roster_id: m.roster_id || null })));
  };
  const saveTeam = async () => {
    if (!tName.trim() && tMembers.length === 0) { toast.error("Adaugă un nume de echipă sau membri"); return; }
    setSavingT(true);
    try {
      const payload = { name: tName, leader: tLeader, notes: tNotes, members: tMembers, size: tMembers.length };
      if (editId) { await api.put(`/montaj/teams/${editId}`, payload); toast.success("Echipă actualizată"); }
      else { await api.post("/montaj/teams", payload); toast.success("Echipă de montaj adăugată"); }
      resetTeam(); loadTeams(); loadCalendar();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingT(false); }
  };
  const delTeam = async (id) => {
    try { await api.delete(`/montaj/teams/${id}`); toast.success("Echipă ștearsă"); loadTeams(); loadCalendar(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---- assign team to project (cu avertizare la dublă alocare) ----
  const assignTeam = async (pid, teamId, force = false) => {
    try {
      const { data } = await api.put(`/projects/${pid}/montaj-team-assign`, { team_id: teamId === "none" ? null : teamId, force });
      if (data && data.conflict) {
        if (window.confirm(`⚠️ ${data.message}`)) return assignTeam(pid, teamId, true);
        return;
      }
      toast.success("Echipă atribuită proiectului"); loadProjects(); loadCalendar(); loadDash();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doAllocate = async () => {
    if (!allocProj || !allocTeam) { toast.error("Alege proiectul și echipa"); return; }
    await assignTeam(allocProj, allocTeam);
  };

  const montajProjects = (projects || [])
    .filter((p) => p.status !== "anulat" && (p.data_montaj || (p.montaj_teams || []).length > 0))
    .sort((a, b) => (a.data_montaj || "9999").localeCompare(b.data_montaj || "9999"));

  const conflictsCount = calendar.filter((c) => (c.conflicts || []).length > 0).length;
  const allProjSorted = (projects || []).filter((p) => p.status !== "anulat").sort((a, b) => (a.code || a.name || "").localeCompare(b.code || b.name || ""));
  const allocCurrent = (() => { const p = (projects || []).find((x) => x.id === allocProj); if (!p || !p.montaj_team_id) return ""; const t = teams.find((x) => x.id === p.montaj_team_id); return t ? t.name : ""; })();

  return (
    <div className="mt-6 space-y-6" data-testid="montaj-hub-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/[0.04] p-4">
        <div>
          <h2 className="flex items-center gap-2 font-head text-lg font-bold"><UsersThree size={20} /> Montaj — echipe, montatori & calendar</h2>
          <p className="mt-1 text-xs text-muted-foreground">Creează echipe și montatori (cu concedii), atribuie echipe pe proiecte și primești alertă când un montator e în concediu chiar în ziua montajului.</p>
        </div>
        <Button onClick={runReminders} disabled={running} data-testid="montaj-hub-reminders-btn"><Bell size={16} className="mr-1.5" /> {running ? "Se trimit…" : "Trimite remindere montaj"}</Button>
      </div>

      {/* ---- Dashboard Montaj (panou unic PM) ---- */}
      <div className="rounded-xl border border-border bg-card p-6" data-testid="montaj-dashboard">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 font-head text-lg font-bold"><Gauge size={20} className="text-primary" weight="bold" /> Dashboard Montaj</h3>
          <div className="flex items-end gap-2">
            <div className="grid gap-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Alertă implicită (ore înainte)</Label>
              <Input type="number" min="1" value={alertHours} onChange={(e) => setAlertHours(e.target.value)} className="h-9 w-28 text-sm" data-testid="montaj-alert-hours" />
            </div>
            <Button size="sm" className="h-9" onClick={saveAlertConfig} disabled={savingAlert} data-testid="montaj-alert-save">{savingAlert ? "…" : "Salvează"}</Button>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/montaj/dashboard/export.pdf?auth=${localStorage.getItem("hgs_token")}`} target="_blank" rel="noreferrer" data-testid="montaj-dashboard-pdf" className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-medium transition-colors hover:border-primary/50 hover:text-primary"><FilePdf size={16} /> Export PDF</a>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-border bg-white/[0.02] p-4" data-testid="dash-stat-allocated">
            <div className="text-2xl font-bold">{(dash?.allocated || []).length}</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><UsersThree size={14} /> Echipe alocate pe proiecte</div>
          </div>
          <div className={`rounded-lg border p-4 ${(dash?.conflicts || []).length > 0 ? "border-red-500/40 bg-red-500/[0.05]" : "border-border bg-white/[0.02]"}`} data-testid="dash-stat-conflicts">
            <div className={`text-2xl font-bold ${(dash?.conflicts || []).length > 0 ? "text-red-300" : ""}`}>{(dash?.conflicts || []).length}</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><Warning size={14} /> Conflicte disponibilitate</div>
          </div>
          <div className={`rounded-lg border p-4 ${(dash?.overruns || []).length > 0 ? "border-red-500/40 bg-red-500/[0.05]" : "border-border bg-white/[0.02]"}`} data-testid="dash-stat-overruns">
            <div className={`text-2xl font-bold ${(dash?.overruns || []).length > 0 ? "text-red-300" : ""}`}>{(dash?.overruns || []).length}</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><Timer size={14} /> Montaje cu timp depășit</div>
          </div>
        </div>

        {/* ---- Alocare echipă pe un proiect (din orice proiect existent) ---- */}
        <div className="mt-5 rounded-lg border border-primary/30 bg-primary/[0.05] p-4" data-testid="montaj-allocate">
          <h4 className="mb-3 flex items-center gap-1.5 text-sm font-bold"><UsersThree size={16} className="text-primary" weight="bold" /> Alocă o echipă pe un proiect</h4>
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Proiect</Label>
              <Select value={allocProj} onValueChange={setAllocProj}>
                <SelectTrigger className="h-9 w-64 text-sm" data-testid="alloc-project"><SelectValue placeholder="Alege proiect…" /></SelectTrigger>
                <SelectContent>
                  {allProjSorted.length === 0 && <SelectItem value="none" disabled>Niciun proiect</SelectItem>}
                  {allProjSorted.map((p) => <SelectItem key={p.id} value={p.id}>{p.code || p.name}{p.data_montaj ? ` · montaj ${fmt(p.data_montaj)}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Echipă</Label>
              <Select value={allocTeam} onValueChange={setAllocTeam}>
                <SelectTrigger className="h-9 w-56 text-sm" data-testid="alloc-team"><SelectValue placeholder="Alege echipă…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— fără echipă —</SelectItem>
                  {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button className="h-9" disabled={!allocProj || !allocTeam} onClick={doAllocate} data-testid="alloc-btn">Alocă echipa</Button>
          </div>
          {allocProj && <p className="mt-2 text-xs text-muted-foreground">Echipă curentă pe acest proiect: <span className="font-medium text-foreground">{allocCurrent || "— niciuna —"}</span></p>}
        </div>

        {(dash?.overruns || []).length > 0 && (
          <div className="mt-4" data-testid="dash-overruns">
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-red-300"><Timer size={15} weight="fill" /> Timp de montaj depășit</h4>
            <div className="space-y-1.5">
              {dash.overruns.map((o) => (
                <Link key={o.project_id} to={`/projects/${o.project_id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-500/30 bg-red-500/[0.04] px-3 py-2 text-sm hover:border-red-500/60" data-testid={`dash-overrun-${o.project_id}`}>
                  <span className="font-semibold">{o.code || o.name}</span>
                  <span className="text-xs text-red-300">Termen depășit: {fmtDT(o.deadline)}{o.team_name ? ` · ${o.team_name}` : ""}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {(dash?.near_expiry || []).length > 0 && (
          <div className="mt-4" data-testid="dash-nearexp">
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-amber-300"><ClockCountdown size={15} weight="fill" /> Montaje pe cale să expire</h4>
            <div className="space-y-1.5">
              {dash.near_expiry.map((o) => (
                <Link key={o.project_id} to={`/projects/${o.project_id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.04] px-3 py-2 text-sm hover:border-amber-500/60" data-testid={`dash-nearexp-${o.project_id}`}>
                  <span className="font-semibold">{o.code || o.name}</span>
                  <span className="text-xs text-amber-300">Expiră în ~{o.hours_left}h ({fmtDT(o.deadline)}){o.team_name ? ` · ${o.team_name}` : ""}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {(dash?.conflicts || []).length > 0 && (
          <div className="mt-4" data-testid="dash-conflicts">
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-red-300"><Warning size={15} weight="fill" /> Conflicte de disponibilitate (concediu / zi liberă în ziua montajului)</h4>
            <div className="space-y-1.5">
              {dash.conflicts.map((c) => (
                <Link key={c.project_id} to={`/projects/${c.project_id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-500/30 bg-red-500/[0.04] px-3 py-2 text-sm hover:border-red-500/60" data-testid={`dash-conflict-${c.project_id}`}>
                  <span className="font-semibold">{c.code || c.name}</span>
                  <span className="text-xs text-red-300">Montaj {fmt(c.data_montaj)}{c.team_name ? ` · ${c.team_name}` : ""} · indisponibili: {(c.unavailable || []).map((u) => `${u.name} (${LEAVE_LABEL[u.type] || u.type})`).join(", ")}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {(dash?.allocated || []).length > 0 && (
          <div className="mt-4" data-testid="dash-allocated">
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><UsersThree size={15} className="text-primary" /> Echipe alocate</h4>
            <div className="space-y-1.5">
              {dash.allocated.map((a) => (
                <div key={a.project_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`dash-alloc-${a.project_id}`}>
                  <span className="font-semibold">{a.code || a.name}</span>
                  <span className="text-xs text-muted-foreground">{a.team_name || "— fără echipă —"}{a.data_montaj ? ` · montaj ${fmt(a.data_montaj)}` : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ---- Calendar montaj 14 zile ---- */}
      <div className="rounded-xl border border-border bg-card p-6" data-testid="montaj-calendar">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-head text-lg font-bold"><CalendarBlank size={20} className="text-primary" /> Calendar montaj — următoarele 14 zile</h3>
          {conflictsCount > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2.5 py-1 text-xs font-bold text-red-300" data-testid="calendar-conflicts-badge"><Warning size={14} weight="fill" /> {conflictsCount} conflict(e) de disponibilitate</span>}
        </div>
        {calendar.length === 0 ? (
          <p className="text-sm text-muted-foreground">Niciun montaj programat în următoarele 14 zile.</p>
        ) : (
          <div className="space-y-2">
            {calendar.map((c) => {
              const hasConflict = (c.conflicts || []).length > 0;
              return (
                <div key={c.project_id} className={`rounded-lg border p-4 ${hasConflict ? "border-red-500/40 bg-red-500/[0.05]" : "border-border"}`} data-testid={`calendar-item-${c.project_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-primary">{fmtShort(c.data_montaj)}</span>
                        <span className="font-semibold">{c.code || c.name}</span>
                        <span className="text-xs text-muted-foreground">{c.days_left <= 0 ? "azi" : `în ${c.days_left}z`}</span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">{c.team_name ? `Echipă: ${c.team_name}` : "Fără echipă atribuită"}</div>
                      {c.members && c.members.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {c.members.map((m, i) => (
                            <span key={i} className={`rounded-full px-2.5 py-1 text-xs ${m.on_leave ? "bg-red-500/15 text-red-300 line-through" : "bg-white/5"}`}>{m.name}{m.on_leave ? " (concediu)" : ""}</span>
                          ))}
                        </div>
                      )}
                      {hasConflict && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-red-300" data-testid={`calendar-conflict-${c.project_id}`}>
                          <Warning size={14} weight="fill" /> Indisponibili în ziua montajului: {c.conflicts.join(", ")}
                        </div>
                      )}
                    </div>
                    <Link to={`/projects/${c.project_id}`} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm hover:border-primary/50 hover:text-primary"><ArrowSquareOut size={15} /> Proiect</Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ---- Echipe + form ---- */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="mb-4 flex items-center gap-2 font-head text-lg font-bold"><UsersThree size={20} /> Echipe de montaj ({teams.length})</h3>
            <div className="space-y-3">
              {teams.length === 0 && <p className="text-sm text-muted-foreground">Nicio echipă de montaj creată. Adaugă prima echipă din dreapta.</p>}
              {teams.map((t) => (
                <div key={t.id} className="rounded-lg border border-border p-4" data-testid={`montaj-team-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold">{t.name} <span className="ml-1 text-xs text-muted-foreground">({t.size} pers.)</span></div>
                      {t.leader && <div className="text-xs text-muted-foreground">Șef echipă: {t.leader}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => startEditTeam(t)} className="text-muted-foreground hover:text-primary" data-testid={`montaj-team-edit-${t.id}`} title="Modifică echipa"><PencilSimple size={16} /></button>
                      <button onClick={() => delTeam(t.id)} className="text-muted-foreground hover:text-red-400" data-testid={`montaj-team-del-${t.id}`}><Trash size={16} /></button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(t.members || []).map((m, i) => <span key={i} className="rounded-full bg-white/5 px-2.5 py-1 text-xs">{m.name}</span>)}
                  </div>
                  {t.notes && <p className="mt-2 text-xs text-muted-foreground">{t.notes}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-4 font-head text-lg font-bold">{editId ? "Modifică echipa" : "Echipă nouă"}</h3>
          <div className="space-y-3">
            <div className="grid gap-1.5"><Label>Nume echipă</Label><Input value={tName} onChange={(e) => setTName(e.target.value)} placeholder="Ex: Echipa A" data-testid="team-name" /></div>
            <div className="grid gap-1.5"><Label>Șef echipă</Label>
              <div className="flex gap-2">
                <Input value={tLeader} onChange={(e) => setTLeader(e.target.value)} placeholder="Nume șef echipă" data-testid="team-leader" />
                <Select value="" onValueChange={(v) => { const m = roster.find((x) => x.id === v); if (m) setTLeader(m.name); }}>
                  <SelectTrigger className="w-36 shrink-0" data-testid="team-leader-from-roster"><SelectValue placeholder="din listă" /></SelectTrigger>
                  <SelectContent>
                    {roster.length === 0 && <SelectItem value="none" disabled>Adaugă în roster mai jos</SelectItem>}
                    {roster.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>Adaugă montator (din listă)</Label>
              <Select value="" onValueChange={addTeamMember}>
                <SelectTrigger data-testid="team-add-montator"><SelectValue placeholder="Alege din montatori…" /></SelectTrigger>
                <SelectContent>
                  {roster.length === 0 && <SelectItem value="none" disabled>Adaugă întâi montatori mai jos</SelectItem>}
                  {roster.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Input value={tManual} onChange={(e) => setTManual(e.target.value)} placeholder="…sau nume liber" onKeyDown={(e) => e.key === "Enter" && addManualMember()} data-testid="team-manual-member" />
              <Button variant="outline" size="icon" onClick={addManualMember} data-testid="team-add-manual"><Plus size={16} /></Button>
            </div>
            {tMembers.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {tMembers.map((m, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-xs text-primary">
                    {m.name}<button onClick={() => removeMember(i)}><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="grid gap-1.5"><Label>Note</Label><Textarea value={tNotes} onChange={(e) => setTNotes(e.target.value)} placeholder="Observații, dotări, mențiuni…" data-testid="team-notes" /></div>
            <Button onClick={saveTeam} disabled={savingT} className="w-full" data-testid="team-save-btn">{savingT ? "Se salvează…" : (editId ? "Salvează modificările" : "Adaugă echipa")}</Button>
            {editId && <Button variant="outline" onClick={resetTeam} className="w-full" data-testid="team-cancel-btn">Anulează editarea</Button>}
          </div>
        </div>
      </div>

      {/* ---- Montatori roster cu concedii ---- */}
      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="mb-4 flex items-center gap-2 font-head text-lg font-bold"><UserPlus size={20} /> Montatori & șefi de echipă (rămân salvați, ștergeți când nu mai sunt angajați)</h3>
        <div className="mb-4 grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
          <Input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Prenume" data-testid="roster-first" />
          <Input value={lName} onChange={(e) => setLName(e.target.value)} placeholder="Nume" data-testid="roster-last" />
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefon (opțional)" data-testid="roster-phone" />
          <Button onClick={addRoster} disabled={savingR} data-testid="roster-add-btn"><Plus size={16} className="mr-1" /> Adaugă</Button>
        </div>
        {roster.length === 0 ? (
          <p className="text-sm text-muted-foreground">Niciun montator adăugat încă.</p>
        ) : (
          <div className="space-y-2">
            {roster.map((m) => (
              <div key={m.id} className="rounded-lg border border-border px-3 py-2.5" data-testid={`roster-${m.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <span className="text-sm font-medium">{m.name}</span>
                    {m.phone && <span className="ml-2 text-xs text-muted-foreground">{m.phone}</span>}
                    {m.on_leave_now && <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-400">ÎN CONCEDIU</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openLeave(m.id)} className="text-xs text-muted-foreground hover:text-primary" data-testid={`roster-leave-open-${m.id}`}>+ concediu / zi liberă</button>
                    <button onClick={() => delRoster(m.id)} className="text-muted-foreground hover:text-red-400" data-testid={`roster-del-${m.id}`}><Trash size={15} /></button>
                  </div>
                </div>
                {(m.leaves || []).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {m.leaves.map((lv) => (
                      <span key={lv.id} className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-0.5 text-[11px] text-muted-foreground">
                        {LEAVE_LABEL[lv.type] || lv.type}: {fmt(lv.start)} → {fmt(lv.end)}
                        <button onClick={() => delLeave(m.id, lv.id)} className="hover:text-red-400" data-testid={`roster-leave-del-${lv.id}`}><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
                {leaveFor === m.id && (
                  <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg border border-border bg-white/[0.02] p-2" data-testid={`roster-leave-form-${m.id}`}>
                    <div className="grid gap-1"><span className="text-[10px] uppercase text-muted-foreground">De la</span><Input type="date" value={lvStart} onChange={(e) => setLvStart(e.target.value)} className="h-8 w-36 text-xs" data-testid="roster-leave-start" /></div>
                    <div className="grid gap-1"><span className="text-[10px] uppercase text-muted-foreground">Până la</span><Input type="date" value={lvEnd} onChange={(e) => setLvEnd(e.target.value)} className="h-8 w-36 text-xs" data-testid="roster-leave-end" /></div>
                    <Select value={lvType} onValueChange={setLvType}>
                      <SelectTrigger className="h-8 w-36 text-xs" data-testid="roster-leave-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{LEAVE_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" className="h-8" onClick={() => addLeave(m.id)} data-testid={`roster-leave-save-${m.id}`}>Salvează</Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => setLeaveFor(null)}>Anulează</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---- Proiecte cu montaj + atribuire echipă ---- */}
      <div>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold"><CalendarCheck size={16} className="text-primary" /> Proiecte cu montaj ({montajProjects.length})</h3>
        {montajProjects.length === 0 ? (
          <p className="text-sm text-muted-foreground">Niciun proiect cu dată de montaj încă. Setează „Dată montaj" în pagina proiectului.</p>
        ) : (
          <div className="space-y-2">
            {montajProjects.map((p) => (
              <div key={p.id} className="rounded-lg border border-border bg-card p-4" data-testid={`montaj-hub-project-${p.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{p.code || p.name}</span>
                      {p.data_montaj && <span className="rounded bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-primary">Montaj: {fmt(p.data_montaj)}</span>}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">{p.name}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={p.montaj_team_id || "none"} onValueChange={(v) => assignTeam(p.id, v)}>
                      <SelectTrigger className="h-9 w-48 text-xs" data-testid={`assign-team-${p.id}`}><SelectValue placeholder="Atribuie echipă…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— fără echipă —</SelectItem>
                        {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Link to={`/projects/${p.id}`} data-testid={`montaj-hub-open-${p.id}`} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm hover:border-primary/50 hover:text-primary"><ArrowSquareOut size={15} /></Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---- Disponibilitate app users (concedii) ---- */}
      {montatori.length > 0 && (
        <div>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold"><Umbrella size={16} className="text-amber-400" /> Disponibilitate angajați Montaj (concedii)</h3>
          <div className="space-y-2">
            {montatori.map((m) => (
              <div key={m.id} className="flex items-start justify-between gap-3 rounded-lg border border-border px-3 py-2" data-testid={`montaj-hub-montator-${m.id}`}>
                <div className="text-sm font-medium">{m.name}{m.on_leave_now && <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-400">ÎN CONCEDIU</span>}</div>
                <div className="text-right text-[11px] text-muted-foreground">
                  {(m.leaves || []).length === 0 ? "fără concedii" : m.leaves.map((lv, i) => <div key={i}>{LEAVE_LABEL[lv.type] || lv.type}: {lv.start} → {lv.end}</div>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
